// ChurchProfilePage — full-screen profile for any entity (church / event /
// event_series / network / ministry). Opens when a card is tapped anywhere in
// the app (Discover, search, add-sermon flow). Replaces the old Church tab's
// Info/Sermons view.
//
// It is self-sufficient: given only an { id, name, ... } entity it re-fetches
// the full entity list (churches table + church_stats) and the sermon list for
// the relevant church id(s), so callers can pass a minimal object.
//
// Props:
//   entity        — the tapped row (needs at least id + name).
//   activeChurchId — the user's home church id (for the MY CHURCH badge + follow lock).
//   sermonNoteChurchIds / setSermonNoteChurchIds — follow state (home at index 0).
//   isAdmin       — moderator/admin (shows "Edit church info").
//   user          — { id, email, full_name }.
//   onOpenSermon(sermonRow) — open the FullSermonSheet.
//   onOpenEntity(entityRow) — navigate to another profile (network → church, etc.).
//   onOpenBibleMap(churchIds) — optional; open the Bible Map for these churches.
//   onEditChurch(entity) — optional; open church settings (admin).
//   onClose()     — dismiss the page.

import { useEffect, useMemo, useState } from "react";
import { Search, ArrowLeft, Calendar } from "lucide-react";
import { supabase } from "../supabase";
import { prefersReducedMotion } from "./StackedSheet";

const GOLD = "var(--gold)";
const NAVY = "var(--ink)";
const SANS = "'Plus Jakarta Sans',system-ui,sans-serif";
const MONO = "'JetBrains Mono',ui-monospace,monospace";
const SERIF = "'Source Serif 4',Georgia,serif";
const BODY_SERIF = "'Source Serif 4',Georgia,serif";

// Entity-type colours (spec §8). event_series is a slightly darker orange.
const ENTITY_COLORS = { church:"#4A90E2", event:"#F59E0B", event_series:"#D97706", network:"#9B6FD4", ministry:"#22C55E" };
const entityColor = (t) => ENTITY_COLORS[t] || "#4A90E2";
const entityBadgeLabel = (t) =>
  t === "event" ? "Event" : t === "event_series" ? "Event Series" :
  t === "network" ? "Network" : t === "ministry" ? "Ministry" : "Church";
const kindOf = (e) => (e && e.entity_type) || "church";

function initialOf(s) { const t = String(s || "").trim(); return t ? t[0].toUpperCase() : "?"; }
function ytThumb(id) { return id ? `https://i.ytimg.com/vi/${id}/mqdefault.jpg` : null; }
function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" }); }
  catch { return String(d); }
}
function yearOf(d) { return d ? String(d).slice(0, 4) : ""; }

function Logo({ url, name, size = 40 }) {
  // A plain `url ? <img onError={hide}/> : initials` ternary leaves the
  // circle blank on a load failure — the initials branch was never
  // rendered, so onError has no fallback text to reveal. Same bug class as
  // CircleLogoOrInitials in ExploreTab.jsx (docs/FIX-CHURCH-LOGOS.md).
  const [failed, setFailed] = useState(false);
  return (
    <div style={{
      width:size, height:size, borderRadius:size/4, overflow:"hidden", flexShrink:0,
      background:"rgba(232,200,122,0.12)", border:"1px solid rgba(232,200,122,0.25)",
      display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:size*0.42, fontWeight:700, color:"var(--gold-ink)", fontFamily:SANS,
    }}>
      {url && !failed
        ? <img src={url} alt="" loading="lazy" referrerPolicy="no-referrer"
            style={{width:"100%",height:"100%",objectFit:"cover"}}
            onError={()=>setFailed(true)}/>
        : initialOf(name)}
    </div>
  );
}

function FollowButton({ following, locked, label, onClick, small }) {
  if (locked) return (
    <span style={{flexShrink:0, fontSize:small?11:12.5, fontWeight:700, color:"var(--stone)", fontFamily:SANS}}>Home</span>
  );
  return (
    <button type="button" onClick={onClick}
      style={{
        flexShrink:0, fontFamily:SANS, fontSize:small?12:13, fontWeight:800, cursor:"pointer",
        borderRadius:9, padding:small?"5px 10px":"8px 14px",
        background: following ? GOLD : "transparent", color: following ? NAVY : "var(--gold-ink)",
        border:`1px solid ${GOLD}`, whiteSpace:"nowrap",
      }}>
      {label || (following ? "Following" : "Follow")}
    </button>
  );
}

function TypeBadge({ type }) {
  return (
    <span style={{
      flexShrink:0, fontSize:9.5, fontWeight:800, letterSpacing:"0.04em", textTransform:"uppercase",
      color:entityColor(type), background:"rgba(31,27,22,0.06)", borderRadius:6, padding:"2px 6px", fontFamily:SANS,
    }}>
      {entityBadgeLabel(type)}
    </span>
  );
}

function SectionLabel({ children, style }) {
  return (
    <div style={{fontSize:11, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--stone)", fontFamily:MONO, ...style}}>
      {children}
    </div>
  );
}

// Robust sermon fetch — tolerates missing optional columns (ai_summary) and the
// profiles join not resolving.
async function fetchSermons(churchIds) {
  if (!churchIds || churchIds.length === 0) return [];
  const REFS = "sermon_refs(*)";
  const attempts = [
    `id,church_id,title,series,preacher,date,youtube_id,youtube_url,duration,ai_summary,created_at,${REFS}`,
    `id,church_id,title,series,preacher,date,youtube_id,youtube_url,duration,created_at,${REFS}`,
    `id,church_id,title,series,preacher,date,youtube_id,youtube_url,created_at`,
  ];
  for (const sel of attempts) {
    const { data, error } = await supabase.from("sermons").select(sel).in("church_id", churchIds).order("date", { ascending: false });
    if (!error) return Array.isArray(data) ? data : [];
  }
  return [];
}

export default function ChurchProfilePage({
  entity, activeChurchId, sermonNoteChurchIds = [], setSermonNoteChurchIds = () => {},
  isAdmin, user, onOpenSermon, onOpenEntity, onOpenBibleMap, onEditChurch, onClose, behind,
}) {
  const [allEntities, setAllEntities] = useState(null);
  const [extra, setExtra] = useState(null);      // { website, youtube_channel_url } for this entity
  const [sermons, setSermons] = useState(null);

  // ── Follow state (mirrors ExploreTab: home church at index 0, rest = extras) ──
  const extras = useMemo(() => (sermonNoteChurchIds || []).filter(id => id && id !== activeChurchId), [sermonNoteChurchIds, activeChurchId]);
  const followingSet = useMemo(() => new Set([...extras, activeChurchId].filter(Boolean)), [extras, activeChurchId]);
  function toggleFollow(id) {
    if (!id || id === activeChurchId) return;
    if (followingSet.has(id)) setSermonNoteChurchIds([activeChurchId, ...extras.filter(x => x !== id)].filter(Boolean));
    else setSermonNoteChurchIds([activeChurchId, ...extras, id].filter(Boolean));
  }
  function followMany(ids, follow) {
    const clean = ids.filter(id => id && id !== activeChurchId);
    if (follow) {
      const add = clean.filter(id => !followingSet.has(id));
      if (add.length) setSermonNoteChurchIds([activeChurchId, ...extras, ...add].filter(Boolean));
    } else {
      const remove = new Set(clean);
      setSermonNoteChurchIds([activeChurchId, ...extras.filter(x => !remove.has(x))].filter(Boolean));
    }
  }

  // ── Load full entity list + stats (same shape as ExploreTab's loader) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const statsRes = await supabase.rpc("church_stats");
      const statsById = new Map();
      if (Array.isArray(statsRes.data)) for (const s of statsRes.data) statsById.set(s.church_id, s);
      let sel = await supabase.from("churches")
        .select("id,name,location,logo_url,network_id,parent_id,entity_type,event_start_date,event_end_date,created_at").order("name");
      if (sel.error) sel = await supabase.from("churches").select("id,name,location,logo_url,network_id,created_at").order("name");
      if (cancelled) return;
      const rows = sel.data || [];
      setAllEntities(rows.map(r => {
        const s = statsById.get(r.id) || {};
        return {
          id:r.id, name:r.name, location:r.location || s.location || null, logo_url:r.logo_url || s.logo_url || null,
          network_id:r.network_id || s.network_id || null, network_name:s.network_name || null,
          parent_id:r.parent_id || null, entity_type:r.entity_type || null,
          event_start_date:r.event_start_date || null, event_end_date:r.event_end_date || null,
          created_at:r.created_at || s.created_at || null,
          sermon_count:Number(s.sermon_count)||0, verse_count:Number(s.verse_count)||0,
        };
      }));
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Website / YouTube (risky columns isolated to a tolerant single-row read) ──
  useEffect(() => {
    let cancelled = false;
    setExtra(null);
    if (!entity?.id) return;
    (async () => {
      // website / youtube live on churches; select tolerantly (drop optional
      // columns if a column doesn't exist so the read still returns the rest).
      const cols = ["id,website,youtube_channel_url,description", "id,website,youtube_channel_url", "id,website"];
      let row = {};
      for (const sel of cols) {
        const { data, error } = await supabase.from("churches").select(sel).eq("id", entity.id).maybeSingle();
        if (!error) { row = data || {}; break; }
      }
      if (!cancelled) setExtra(row);
    })();
    return () => { cancelled = true; };
  }, [entity?.id]);

  // ── Resolve this entity + its members / children ──
  const self = useMemo(() => (allEntities || []).find(e => e.id === entity.id) || entity, [allEntities, entity]);
  const kind = kindOf(self);
  const isNetwork = kind === "network";
  const isSeries = kind === "event_series";
  const isEvent = kind === "event";

  const members = useMemo(() =>
    isNetwork ? (allEntities || []).filter(e => e.id !== self.id && (e.parent_id === self.id || e.network_id === self.id)) : [],
    [allEntities, isNetwork, self.id]);
  const children = useMemo(() =>
    isSeries ? (allEntities || []).filter(e => e.parent_id === self.id) : [],
    [allEntities, isSeries, self.id]);
  const parentNetwork = useMemo(() => {
    const pid = self.parent_id || self.network_id;
    if (!pid) return null;
    return (allEntities || []).find(e => e.id === pid) || (self.network_name ? { id: pid, name: self.network_name, entity_type: "network" } : null);
  }, [allEntities, self]);

  // Church ids whose sermons belong to this profile.
  const targetIds = useMemo(() => {
    if (isNetwork) return [...new Set([self.id, ...members.map(m => m.id)])];
    if (isSeries)  return [...new Set([self.id, ...children.map(c => c.id)])];
    return [self.id];
  }, [isNetwork, isSeries, self.id, members, children]);
  const targetKey = targetIds.join(",");

  useEffect(() => {
    let cancelled = false;
    setSermons(null);
    (async () => {
      const rows = await fetchSermons(targetIds);
      if (!cancelled) setSermons(rows);
    })();
    return () => { cancelled = true; };
  }, [targetKey]);

  // Aggregate counts.
  const sermonCount = sermons ? sermons.length : (
    isNetwork ? members.reduce((s,m)=>s+(m.sermon_count||0),0) + (self.sermon_count||0)
    : isSeries ? children.reduce((s,c)=>s+(c.sermon_count||0),0) + (self.sermon_count||0)
    : (self.sermon_count||0));
  const verseCount = useMemo(() => {
    if (!sermons) return isNetwork ? members.reduce((s,m)=>s+(m.verse_count||0),0) : (self.verse_count||0);
    const set = new Set();
    for (const s of sermons) for (const r of (s.sermon_refs || [])) set.add(`${r.book} ${r.chapter}:${r.verse}`);
    return set.size;
  }, [sermons, isNetwork, members, self]);

  // ── Tabs ──
  const tabs = isNetwork ? ["churches", "sermons", "info"] : ["sermons", "info"];
  const [tab, setTab] = useState(isNetwork ? "churches" : "sermons");
  useEffect(() => { setTab(isNetwork ? "churches" : "sermons"); }, [isNetwork, entity.id]);

  const isHome = self.id === activeChurchId;
  const followingSelf = followingSet.has(self.id);
  const networkFollowingAll = isNetwork && followingSet.has(self.id) && members.every(m => followingSet.has(m.id));

  function headerFollow() {
    if (isNetwork) {
      const ids = [self.id, ...members.map(m => m.id)];
      followMany(ids, !networkFollowingAll);
    } else {
      toggleFollow(self.id);
    }
  }

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:400, background:"var(--cream)", display:"flex", flexDirection:"column",
      fontFamily:SANS, color:"var(--ink)",
      transform: behind ? "translateY(-10px) scale(0.93)" : "none",
      filter: behind ? "brightness(0.82)" : "none",
      borderRadius: behind ? 26 : 0,
      transition: prefersReducedMotion() ? "none" : "transform .42s cubic-bezier(.2,.8,.2,1), filter .42s ease, border-radius .42s ease",
    }}>
      {/* ── HEADER ── */}
      <div style={{flexShrink:0, background:"var(--white)", borderBottom:"1px solid var(--border)"}}>
        <div style={{maxWidth:680, margin:"0 auto", padding:"12px 16px 16px", boxSizing:"border-box"}}>
          <div style={{display:"flex", alignItems:"center", gap:12}}>
            <button type="button" onClick={onClose} aria-label="Back"
              style={{display:"flex", alignItems:"center", gap:6, background:"none", border:"none", color:"var(--stone)", cursor:"pointer", fontSize:14, fontFamily:SANS, padding:0}}>
              <ArrowLeft size={20}/>
            </button>
            <div style={{flex:1, minWidth:0, textAlign:"center", fontSize:13.5, fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{self.name}</div>
            <FollowButton
              following={isNetwork ? networkFollowingAll : followingSelf}
              locked={isHome}
              label={isNetwork ? (networkFollowingAll ? "Following all" : "Follow all") : undefined}
              onClick={headerFollow}/>
          </div>

          <div style={{display:"flex", alignItems:"center", gap:14, marginTop:16}}>
            <Logo url={self.logo_url} name={self.name} size={56}/>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:26, fontWeight:600, color:"var(--ink)", fontFamily:SERIF, lineHeight:1.1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{self.name}</div>
              <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:5}}>
                {self.location && <span style={{fontSize:12.5, color:"var(--stone)"}}>{self.location}</span>}
                <TypeBadge type={kind}/>
                {isHome && (
                  <span style={{fontSize:9.5, fontWeight:800, letterSpacing:"0.04em", textTransform:"uppercase", color:"var(--ink)", background:GOLD, borderRadius:6, padding:"2px 7px"}}>My Church</span>
                )}
              </div>
            </div>
          </div>

          {/* Sub-context line */}
          <div style={{display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", marginTop:12, fontSize:12.5, color:"var(--stone)"}}>
            {parentNetwork && (
              <button type="button" onClick={()=>onOpenEntity?.(parentNetwork)}
                style={{background:"none", border:"none", padding:0, cursor:"pointer", color:"var(--gold-ink)", fontSize:12.5, fontWeight:700, fontFamily:SANS}}>
                Part of {parentNetwork.name}
              </button>
            )}
            {(isEvent || isSeries) && self.event_start_date && (
              <span>{fmtDate(self.event_start_date)}{self.event_end_date ? ` – ${fmtDate(self.event_end_date)}` : ""}</span>
            )}
            <span>{sermonCount.toLocaleString()} sermon{sermonCount===1?"":"s"}</span>
            <span>·</span>
            <span>{verseCount.toLocaleString()} verse{verseCount===1?"":"s"}{isNetwork ? "" : " covered"}</span>
            {isNetwork && <span>· {members.length} church{members.length===1?"":"es"}</span>}
          </div>

          {/* Tabs */}
          <div style={{display:"flex", gap:4, marginTop:16}}>
            {tabs.map(t => {
              const on = tab === t;
              return (
                <button key={t} type="button" onClick={()=>setTab(t)}
                  style={{
                    flex:1, background:"none", border:"none", cursor:"pointer", padding:"9px 4px",
                    fontSize:12, fontWeight:on?800:600, letterSpacing:"0.06em", textTransform:"uppercase",
                    color:on?"var(--gold-ink)":"var(--stone)", borderBottom:`2px solid ${on?GOLD:"transparent"}`, fontFamily:SANS,
                  }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch"}}>
        <div style={{maxWidth:680, margin:"0 auto", padding:"16px 16px max(28px, env(safe-area-inset-bottom))", boxSizing:"border-box"}}>
          {tab === "sermons" && (
            <SermonsTab sermons={sermons} isSeries={isSeries} children={children} onOpenSermon={onOpenSermon}/>
          )}
          {tab === "churches" && isNetwork && (
            <NetworkChurchesTab members={members} followingSet={followingSet} activeChurchId={activeChurchId}
              onToggle={toggleFollow} onOpenEntity={onOpenEntity} verseCount={verseCount}
              onOpenBibleMap={onOpenBibleMap ? ()=>onOpenBibleMap(targetIds) : null}/>
          )}
          {tab === "info" && (
            <InfoTab self={self} kind={kind} extra={extra} parentNetwork={parentNetwork}
              isHome={isHome} isAdmin={isAdmin} children={children} onOpenEntity={onOpenEntity}
              onEdit={onEditChurch ? ()=>onEditChurch(self) : null}/>
          )}
        </div>
      </div>
    </div>
  );
}

// ── SERMONS TAB ──────────────────────────────────────────────────────────────
function SermonsTab({ sermons, isSeries, children, onOpenSermon }) {
  const [q, setQ] = useState("");
  const [series, setSeries] = useState("__all__");
  const [year, setYear] = useState("__all__");
  const [childId, setChildId] = useState("__all__");   // series year pills
  const [limit, setLimit] = useState(20);

  const seriesNames = useMemo(() => {
    const s = new Set();
    for (const x of (sermons || [])) if (x.series) s.add(x.series);
    return [...s].sort();
  }, [sermons]);
  const years = useMemo(() => {
    const s = new Set();
    for (const x of (sermons || [])) if (x.date) s.add(yearOf(x.date));
    return [...s].sort().reverse();
  }, [sermons]);

  const filtered = useMemo(() => {
    if (!sermons) return null;
    const ql = q.trim().toLowerCase();
    return sermons.filter(s => {
      if (childId !== "__all__" && s.church_id !== childId) return false;
      if (series !== "__all__" && s.series !== series) return false;
      if (year !== "__all__" && yearOf(s.date) !== year) return false;
      if (ql && !(String(s.title||"").toLowerCase().includes(ql) || String(s.preacher||"").toLowerCase().includes(ql))) return false;
      return true;
    });
  }, [sermons, q, series, year, childId]);

  useEffect(() => { setLimit(20); }, [q, series, year, childId]);

  if (sermons === null) return <div style={{padding:"30px 0", textAlign:"center", fontSize:13, color:"var(--stone)"}}>Loading sermons…</div>;
  if (sermons.length === 0) return <Empty>No sermons yet for this church.</Empty>;

  const shown = (filtered || []).slice(0, limit);

  return (
    <div>
      {/* Search + filters */}
      <div style={{position:"relative", marginBottom:10}}>
        <Search size={15} color="var(--stone)" style={{position:"absolute", left:12, top:"50%", transform:"translateY(-50%)"}}/>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search title or preacher…"
          style={{width:"100%", boxSizing:"border-box", background:"var(--white)", border:"1px solid var(--border-strong)", borderRadius:10, padding:"10px 12px 10px 34px", color:"var(--ink)", fontSize:14, fontFamily:SANS, outline:"none"}}/>
      </div>

      {isSeries && children.length > 0 ? (
        <div style={{display:"flex", gap:8, overflowX:"auto", paddingBottom:8, marginBottom:12, WebkitOverflowScrolling:"touch"}}>
          <YearPill on={childId==="__all__"} onClick={()=>setChildId("__all__")}>All years</YearPill>
          {children.map(c => (
            <YearPill key={c.id} on={childId===c.id} onClick={()=>setChildId(c.id)}>{c.name}</YearPill>
          ))}
        </div>
      ) : (
        (seriesNames.length > 0 || years.length > 1) && (
          <div style={{display:"flex", gap:8, marginBottom:12, flexWrap:"wrap"}}>
            {seriesNames.length > 0 && (
              <Dropdown value={series} onChange={setSeries}
                options={[["__all__","All series"], ...seriesNames.map(s=>[s,s])]}/>
            )}
            {years.length > 1 && (
              <Dropdown value={year} onChange={setYear}
                options={[["__all__","All years"], ...years.map(y=>[y,y])]}/>
            )}
          </div>
        )
      )}

      {filtered && filtered.length === 0 ? (
        <Empty>No sermons match your filters.</Empty>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:10}}>
          {shown.map(s => <SermonCard key={s.id} s={s} onOpen={()=>onOpenSermon?.(s)}/>)}
        </div>
      )}

      {filtered && filtered.length > limit && (
        <button type="button" onClick={()=>setLimit(l => l + 20)}
          style={{display:"block", width:"100%", marginTop:14, background:"var(--white)", border:"1px solid var(--border-strong)", color:"var(--gold-ink)", borderRadius:10, padding:"11px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:SANS}}>
          Load more
        </button>
      )}
    </div>
  );
}

function SermonCard({ s, onOpen }) {
  const thumb = ytThumb(s.youtube_id);
  const refs = (s.sermon_refs || []).length;
  const when = s.date ? fmtDate(s.date) : "";
  return (
    <button type="button" onClick={onOpen}
      style={{display:"flex", gap:12, width:"100%", textAlign:"left", background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, padding:12, cursor:"pointer", fontFamily:SANS}}>
      {thumb ? (
        <div style={{flexShrink:0, width:104, height:60, borderRadius:8, overflow:"hidden", background:"#000"}}>
          <img src={thumb} alt="" loading="lazy" referrerPolicy="no-referrer"
            style={{width:"100%", height:"100%", objectFit:"cover"}} onError={e=>{e.currentTarget.style.display="none";}}/>
        </div>
      ) : (
        <div style={{flexShrink:0, width:104, height:60, borderRadius:8, background:"rgba(232,200,122,0.1)", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--gold-ink)"}}>
          <Calendar size={20}/>
        </div>
      )}
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:15, fontStyle:"italic", fontFamily:BODY_SERIF, color:"var(--ink)", lineHeight:1.25, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>{s.title || "Sermon"}</div>
        {s.series && <div style={{fontSize:10.5, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--gold-ink)", fontFamily:MONO, marginTop:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{s.series}</div>}
        <div style={{display:"flex", alignItems:"center", gap:8, marginTop:5}}>
          <span style={{fontSize:11.5, color:"var(--stone)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
            {[s.preacher, when].filter(Boolean).join(" · ")}
          </span>
          {refs > 0 && (
            <span style={{flexShrink:0, marginLeft:"auto", fontSize:10.5, fontWeight:700, color:"var(--gold-ink)", background:"rgba(232,200,122,0.12)", border:"1px solid rgba(232,200,122,0.25)", borderRadius:7, padding:"2px 7px", whiteSpace:"nowrap"}}>
              {refs} ref{refs===1?"":"s"}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── NETWORK CHURCHES TAB ─────────────────────────────────────────────────────
function NetworkChurchesTab({ members, followingSet, activeChurchId, onToggle, onOpenEntity, verseCount, onOpenBibleMap }) {
  const totalSermons = members.reduce((s,m)=>s+(m.sermon_count||0),0);
  return (
    <div>
      <div style={{display:"flex", gap:10, marginBottom:16, flexWrap:"wrap"}}>
        <Stat label="Churches" value={String(members.length)}/>
        <Stat label="Sermons" value={totalSermons.toLocaleString()}/>
        <Stat label="Verses covered" value={verseCount.toLocaleString()}/>
      </div>
      {onOpenBibleMap && (
        <button type="button" onClick={onOpenBibleMap}
          style={{display:"block", width:"100%", marginBottom:16, background:"transparent", border:`1px solid ${GOLD}`, color:"var(--gold-ink)", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:SANS}}>
          See in Bible Map →
        </button>
      )}
      <SectionLabel style={{marginBottom:12}}>Member churches</SectionLabel>
      {members.length === 0 ? <Empty>No member churches yet.</Empty> : (
        <div className="cpp-grid" style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(150px, 1fr))", gap:10}}>
          {members.map(m => {
            const following = m.id === activeChurchId || followingSet.has(m.id);
            return (
              <div key={m.id} style={{background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:8}}>
                <button type="button" onClick={()=>onOpenEntity?.(m)}
                  style={{display:"flex", alignItems:"center", gap:10, background:"none", border:"none", padding:0, cursor:"pointer", textAlign:"left"}}>
                  <Logo url={m.logo_url} name={m.name} size={34}/>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13.5, fontWeight:700, color:"var(--ink)", fontFamily:SERIF, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{m.name}</div>
                    {m.location && <div style={{fontSize:11, color:"var(--stone)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{m.location}</div>}
                  </div>
                </button>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:6}}>
                  <span style={{fontSize:11, color:"var(--stone)"}}>{(m.sermon_count||0).toLocaleString()} sermon{m.sermon_count===1?"":"s"}</span>
                  <FollowButton small following={following} locked={m.id === activeChurchId} onClick={()=>onToggle(m.id)}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── INFO TAB ─────────────────────────────────────────────────────────────────
function InfoTab({ self, kind, extra, parentNetwork, isHome, isAdmin, children, onOpenEntity, onEdit }) {
  const website = extra?.website;
  const youtube = extra?.youtube_channel_url;
  const description = extra?.description || self.description;
  const rows = [];
  if (self.location) rows.push(["Location", self.location]);
  if (website) rows.push(["Website", <Link href={website}>{website.replace(/^https?:\/\//, "")}</Link>]);
  if (youtube) rows.push(["YouTube", <Link href={youtube}>Open channel</Link>]);
  if (parentNetwork) rows.push([kind === "event_series" ? "Series" : "Network",
    <button type="button" onClick={()=>onOpenEntity?.(parentNetwork)} style={{background:"none", border:"none", padding:0, color:"var(--gold-ink)", fontWeight:700, cursor:"pointer", fontFamily:SANS, fontSize:13.5}}>{parentNetwork.name}</button>]);
  if (self.created_at) rows.push(["Joined", fmtDate(String(self.created_at).slice(0,10))]);

  const empty = !description && rows.length === 0;

  return (
    <div>
      {description && (
        <p style={{fontSize:14.5, lineHeight:1.6, color:"var(--ink)", margin:"0 0 18px", fontFamily:BODY_SERIF}}>{description}</p>
      )}

      {kind === "event_series" && children.length > 0 && (
        <div style={{marginBottom:18}}>
          <SectionLabel style={{marginBottom:10}}>Years</SectionLabel>
          <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
            {children.map(c => (
              <button key={c.id} type="button" onClick={()=>onOpenEntity?.(c)}
                style={{background:"var(--white)", border:"1px solid var(--border-strong)", borderRadius:999, padding:"7px 14px", fontSize:12.5, fontWeight:700, color:"var(--ink)", cursor:"pointer", fontFamily:SANS}}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {empty ? (
        <Empty>No information available yet.</Empty>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:0, background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden"}}>
          {rows.map(([label, val], i) => (
            <div key={i} style={{display:"flex", gap:12, padding:"13px 16px", borderTop:i?"1px solid var(--border)":"none"}}>
              <div style={{flexShrink:0, width:90, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--stone)", fontFamily:MONO, paddingTop:1}}>{label}</div>
              <div style={{flex:1, minWidth:0, fontSize:13.5, color:"var(--ink)", wordBreak:"break-word"}}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {isHome && isAdmin && onEdit && (
        <button type="button" onClick={onEdit}
          style={{display:"block", marginTop:18, background:"none", border:"none", color:"var(--gold-ink)", fontSize:13.5, fontWeight:700, cursor:"pointer", fontFamily:SANS, padding:0, textDecoration:"underline"}}>
          Edit church info →
        </button>
      )}
    </div>
  );
}

// ── tiny helpers ─────────────────────────────────────────────────────────────
function Link({ href, children }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" style={{color:"var(--gold-ink)", fontWeight:600, textDecoration:"underline"}}>{children}</a>;
}
function YearPill({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{flexShrink:0, background:on?GOLD:"var(--white)", color:on?"var(--ink)":"var(--stone)", border:on?"none":"1px solid var(--border-strong)", borderRadius:999, padding:"7px 13px", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", fontFamily:SANS}}>
      {children}
    </button>
  );
}
function Dropdown({ value, onChange, options }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{background:"var(--white)", color:"var(--ink)", border:"1px solid var(--border-strong)", borderRadius:9, padding:"8px 10px", fontSize:12.5, fontWeight:600, fontFamily:SANS, outline:"none", cursor:"pointer", maxWidth:180}}>
      {options.map(([v, l]) => <option key={v} value={v} style={{background:"var(--white)"}}>{l}</option>)}
    </select>
  );
}
function Stat({ label, value }) {
  return (
    <div style={{flex:1, minWidth:96, background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, padding:"12px 14px"}}>
      <div style={{fontSize:10.5, color:"var(--stone)", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, fontFamily:MONO}}>{label}</div>
      <div style={{fontSize:19, fontWeight:800, color:"var(--gold-ink)", marginTop:4, lineHeight:1.1}}>{value}</div>
    </div>
  );
}
function Empty({ children }) {
  return <div style={{padding:"22px 14px", textAlign:"center", fontSize:13, color:"var(--stone)", background:"rgba(31,27,22,0.03)", border:"1px solid var(--border)", borderRadius:10, lineHeight:1.5}}>{children}</div>;
}
